import { zh } from './locale-zh.js';
export interface Brand {
  name: string;
  ticker: string;
  aliases?: string[];
  nameZh?: string;
  category?: string;
  receiptScope?: string;
  issuerAsset?: { chainId: number; symbol: string; address: string };
  quoteScreening?: { status: 'indicative-pass'; checkedAt: string; loss100Pct: number; loss1000Pct: number };
}

export const fallbackBrands: Brand[] = [
  { name: 'Apple', ticker: 'AAPL', aliases: ['Apple Store'], category: 'Shopping', receiptScope: 'Direct Apple purchases with a USD receipt.' },
  { name: 'Amazon', ticker: 'AMZN', aliases: ['Whole Foods'], category: 'Shopping', receiptScope: 'Amazon or Whole Foods purchases with a USD receipt.' },
  { name: 'Costco', ticker: 'COST', aliases: ['Costco Wholesale'], category: 'Retail', receiptScope: 'Costco purchases with a USD receipt.' },
  { name: 'GameStop', ticker: 'GME', aliases: [], category: 'Shopping', receiptScope: 'GameStop purchases with a USD receipt.' },
];

export function normalizeBrands(value: unknown): Brand[] {
  const source = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  return source.filter((b): b is Brand => {
    if (!b || typeof b !== 'object' || typeof b.name !== 'string' || typeof b.ticker !== 'string' || !b.name.trim() || !b.ticker.trim()) return false;
    const key = `${b.name.toLowerCase()}|${b.ticker.toUpperCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map(b => ({ ...b, name: b.name.trim(), ticker: b.ticker.trim().toUpperCase(), aliases: Array.isArray(b.aliases) ? b.aliases.filter(a => typeof a === 'string') : [], category: typeof b.category === 'string' && b.category ? b.category : 'Shopping', receiptScope: typeof b.receiptScope === 'string' ? b.receiptScope : undefined, nameZh: typeof b.nameZh === 'string' ? b.nameZh : undefined }));
}

export function brandMatches(brand: Brand, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  return !needle || [brand.name, brand.nameZh, zh(brand.name), brand.ticker, brand.category, zh(brand.category), brand.receiptScope, zh(brand.receiptScope), ...(brand.aliases || [])].filter(Boolean).join(' ').toLocaleLowerCase().includes(needle);
}

/** A read-only catalogue. Pricing snapshots never change eligibility or enable delivery. */
export function mountBrandDirectory(elements: { list: HTMLElement; search: HTMLInputElement; category: HTMLSelectElement; count: HTMLElement }, initial: Brand[]) {
  let brands = normalizeBrands(initial);
  let examples = true;
  const render = () => {
    const visible = brands.filter(b => brandMatches(b, elements.search.value) && (!elements.category.value || b.category === elements.category.value));
    elements.count.textContent = `${visible.length} / ${brands.length} 个${examples ? '示例' : '已选择'}品牌`;
    const fragment = document.createDocumentFragment();
    for (const brand of visible) {
      const row = document.createElement('article'); row.className = 'brand-entry';
      const heading = document.createElement('div'); heading.className = 'brand-entry-heading';
      const name = document.createElement('strong'); name.textContent = zh(brand.name);
      const ticker = document.createElement('b'); ticker.textContent = zh(`→ ${brand.ticker}`);
      heading.append(name, ticker);
      const category = document.createElement('small'); category.className = 'brand-entry-category'; category.textContent = zh(brand.category || 'Shopping');
      const scope = document.createElement('p'); scope.textContent = zh(brand.receiptScope || 'Matching merchant on a USD receipt; eligibility remains subject to review.');
      const state = document.createElement('small'); state.className = 'brand-entry-state'; state.textContent = zh(examples ? 'Example mapping · delivery pending' : 'Receipt matching · delivery pending');
      row.append(heading, category, scope, state); fragment.append(row);
    }
    if (!visible.length) {
      const empty = document.createElement('p'); empty.className = 'brand-empty'; empty.textContent = zh(brands.length ? 'No matching brand. Try a merchant name, ticker or another category.' : 'No receipt brands are configured. Please check again later.'); fragment.append(empty);
    }
    elements.list.replaceChildren(fragment);
  };
  const update = (value: Brand[], isExample = false) => {
    brands = normalizeBrands(value); examples = isExample;
    const previous = elements.category.value;
    elements.category.replaceChildren(new Option(zh('All categories'), ''));
    for (const category of [...new Set(brands.map(b => b.category || 'Shopping'))].sort()) elements.category.add(new Option(zh(category), category));
    if ([...elements.category.options].some(option => option.value === previous)) elements.category.value = previous;
    render();
  };
  elements.search.addEventListener('input', render);
  elements.category.addEventListener('change', render);
  update(brands, true);
  return update;
}
